import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchScopeControlCounts,
  fetchScopeEntities,
  fetchScopeMateriality,
  fetchScopeOverdueSummary,
  fetchScopePlannedCompleted,
  fetchScopeRiskSummary,
  fetchScopeSystems,
  fetchScopeTestingStatusRollup,
  type ScopeControlCountsResponse,
  type ScopeEntitiesResponse,
  type ScopeMaterialityResponse,
  type ScopeOverdueSummaryResponse,
  type ScopePlannedCompletedResponse,
  type ScopeRiskSummaryResponse,
  type ScopeSystemsResponse,
  type ScopeTestingStatusRollupResponse,
} from '../../request/analytics/scopeReports.ts';
import { analyticsScopeQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the cross-persona `/api/analytics/scope/*`
 * endpoints (feedback round 2026-08) — shared "scope & coverage" data reused
 * across the CFO, CAE, Controller, and Auditor dashboards (and the
 * self-serve gallery). Each report is its own independent query, same
 * pattern as every other persona's hooks file.
 */

export function useScopeControlCounts(): UseQueryResult<ScopeControlCountsResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.controlCounts(), queryFn: fetchScopeControlCounts });
}

export function useScopeRiskSummary(): UseQueryResult<ScopeRiskSummaryResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.riskSummary(), queryFn: fetchScopeRiskSummary });
}

export function useScopeSystems(): UseQueryResult<ScopeSystemsResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.systems(), queryFn: fetchScopeSystems });
}

export function useScopeEntities(): UseQueryResult<ScopeEntitiesResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.entities(), queryFn: fetchScopeEntities });
}

export function useScopeMateriality(): UseQueryResult<ScopeMaterialityResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.materiality(), queryFn: fetchScopeMateriality });
}

export function useScopePlannedCompleted(): UseQueryResult<ScopePlannedCompletedResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.plannedCompleted(), queryFn: fetchScopePlannedCompleted });
}

export function useScopeOverdueSummary(): UseQueryResult<ScopeOverdueSummaryResponse, Error> {
  return useQuery({ queryKey: analyticsScopeQueryKeys.overdueSummary(), queryFn: fetchScopeOverdueSummary });
}

export function useScopeTestingStatusRollup(): UseQueryResult<ScopeTestingStatusRollupResponse, Error> {
  return useQuery({
    queryKey: analyticsScopeQueryKeys.testingStatusRollup(),
    queryFn: fetchScopeTestingStatusRollup,
  });
}
